/**
 * Data Fetching Service for Middleware
 * Handles server-side data fetching for route guards.
 *
 * On SSR these helpers run inside nitro handlers where `useNuxtApp()` is not
 * reachable from within service methods. Callers pass a pre-resolved nuxtApp
 * captured earlier in the request lifecycle (the route middleware) so the
 * auth-token attachment and 401 refresh chain in BaseService work end-to-end.
 */

import type { SSRNuxtAppLike } from '@common/http-layer/service/base.service'
import type { TabConfigurationItem } from '@communities/groups-layer/types/types'

export interface GroupData {
  groupDetail?: {
    _id: string
    locationId: string
    slug: string
    settings?: {
      groupType?: string
      tabsConfiguration?: TabConfigurationItem[] | string[]
    }
  }
}

export interface UserMembershipData {
  _id: string
  contactId: string
  groupId: string
  locationId: string
  status: string
  role: string
}

/**
 * Fetches group data by slug
 * @param groupSlug - Group slug identifier
 * @param ssrNuxtApp - Optional pre-resolved nuxtApp for SSR auth/refresh
 * @returns Promise<GroupData | null> - Group data or null if not found
 */
export async function fetchGroupData(groupSlug: string, ssrNuxtApp?: SSRNuxtAppLike): Promise<GroupData | null> {
  try {
    const { GroupsService } = await import('@communities/groups-layer/services/groups.service')
    const groupsService = new GroupsService(ssrNuxtApp)

    const response = await groupsService.getGroupBySlug(groupSlug)

    return response
  } catch (error) {
    console.error('❌ [Data Service] Failed to fetch group data:', error)
    throw error
  }
}

function httpStatusOf(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') return undefined
  const e = error as Record<string, unknown>
  const direct = e.statusCode ?? e.status
  if (typeof direct === 'number') return direct
  const response = e.response
  if (response && typeof response === 'object') {
    const rs = (response as Record<string, unknown>).status ?? (response as Record<string, unknown>).statusCode
    if (typeof rs === 'number') return rs
  }
  return undefined
}

/**
 * Fetches user membership data for a group.
 *
 * Returns `null` when the user has no membership (404). Re-throws all other errors
 * so the caller can distinguish a genuine "not a member" from a transient API failure —
 * critical for ban checks on public groups, where `null` membership must not silently
 * bypass a `BANNED` status.
 */
export async function fetchUserMembershipData(
  groupId: string,
  locationId: string,
  ssrNuxtApp?: SSRNuxtAppLike
): Promise<UserMembershipData | null> {
  try {
    console.log('🔄 [Data Service] Fetching user membership for group:', groupId)

    const { CommunityUserService } = await import('@communities/community-user-layer/services/community-user.service')
    const communityUserService = new CommunityUserService(ssrNuxtApp)

    const response = await communityUserService.getUserDetailsForCurrentGroup(groupId, locationId)
    console.log('✅ [Data Service] User membership data fetched successfully')

    return response
  } catch (error) {
    if (httpStatusOf(error) === 404) {
      return null
    }
    console.error('❌ [Data Service] Failed to fetch user membership:', error)
    throw error
  }
}
